import React, { useContext, useEffect, useRef, useState, useSyncExternalStore } from "react";
import "./App.css";
import ActGeo from "@actech-npm/actgeowebsdk";
import Footer from "./components/Footer";
import AppStoreLinksPanel from "./components/AppStoreLinksPanel";
import {
  Box,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ACTButton from "./components/ACTButton";
import HomePanel from "./components/HomePanel";
import VerifiedPanel from "./components/VerifiedPanel";
import FailedPanel from "./components/FailedPanel";
import SessionEndedPanel from "./components/SessionEndedPanel";
import ACTAlertWarning from "./components/ACTAlertWarning";
import Header from "./components/Header";
import { AppContext } from "./context/AppContext";
import FootballGraphic from "../src/assets/images/graphic_football_default.png";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import toggleVisibilityRef from "./components/ToggleVisibilityRef";
import TestBetPanel from "./components/TestBetPanel";
import { TEST_BET_ENABLED, createDemoSession, listDemoApps } from "./demoBackend";
import { getState as getSsoState, start as startSso, subscribe as subscribeToSso } from "./ssoAuth";

// The Geo API the SDK's web session talks to (ScanToken, WebSocket)
const GEO_BASE_URL = process.env.REACT_APP_SDK_BASE_URL;
// SDK console logging, off unless REACT_APP_SDK_LOGGING=true
const SDK_LOGGING = process.env.REACT_APP_SDK_LOGGING === "true";

function App() {
  const {
    selectedAPIKey,
    setSelectedAPIKey,
    accountId,
    setAccountId,
    setValidationStatus,
    sessionEnded,
    showWebPage,
  } = useContext(AppContext);
  const [qrCode, setQrCode] = useState("");
  const [validationResult, setValidationResult] = useState(null);
  const [expiryTime, setExpiryTime] = useState(null);
  const [refreshQrCode, setRefreshQrCode] = useState(false);
  // The signed-in ACT.SSO user (ssoAuth.js) and the jurisdictions the demo backend allows them
  const sso = useSyncExternalStore(subscribeToSso, getSsoState);
  const [jurisdictions, setJurisdictions] = useState([]);
  const [loadingJurisdictions, setLoadingJurisdictions] = useState(false);
  const [jurisdictionsError, setJurisdictionsError] = useState(null);
  const appsRequest = useRef(0); // only the current user's list may show
  const canCreateQR = !!selectedAPIKey && accountId.trim().length > 0 && sso.status === "signedIn";
  const timerRef = useRef();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [showAlert, setShowAlert] = useState(true);
  const backgroundStyle = {
    // backgroundImage: `url(${ACTBackgroundImage})`,
    background: "rgb(217,220,225)",
    background:
      "linear-gradient(180deg, rgba(217,220,225,1) 0%, rgba(255,255,255,1) 100%)",
    backgroundSize: "cover",
    backgroundPosition: "center",
  };

  const [isHomePanel, setIsHomePanel] = useState(true);
  const [isVerifiedPanel, setIsVerifiedPanel] = useState(false);
  const [isFailedPanel, setIsFailedPanel] = useState(false);
  const [isEndedPanel, setIsEndedPanel] = useState(false);
  const [sessionData, setSessionData] = useState(null);
  const [goToHomePanel, setGoToHomePanel] = useState(false);
  const [deeplink, setDeeplink] = useState(null);
  const [visible, setVisible] = useState(toggleVisibilityRef.isVisible);

  useEffect(() => {
    const unsubscribe = toggleVisibilityRef.subscribe(setVisible);
    return unsubscribe;
  }, []);

  const goToHome = () => {
    setGoToHomePanel(true);
    setIsHomePanel(true);
    setIsVerifiedPanel(false);
    setIsFailedPanel(false);
    setIsEndedPanel(false);
  };

  const handleHideAlert = () => {
    setShowAlert(false);
  };

  const showHomePanel = () => {
    setIsHomePanel(true);
    setIsVerifiedPanel(false);
    setIsFailedPanel(false);
    setIsEndedPanel(false);
  };

  const showVerifedPanel = () => {
    setIsHomePanel(false);
    setIsVerifiedPanel(true);
    setIsFailedPanel(false);
    setIsEndedPanel(false);
  };

  const showFailedPanel = () => {
    setIsHomePanel(false);
    setIsVerifiedPanel(false);
    setIsFailedPanel(true);
    setIsEndedPanel(false);
  };

  const showEndedPanel = () => {
    setIsHomePanel(false);
    setIsVerifiedPanel(false);
    setIsFailedPanel(false);
    setIsEndedPanel(true);
  };

  // QR creation is triggered explicitly via the "Create QR" button in HomePanel.

  useEffect(() => {
    if (showWebPage) {
      initialiseSDKSession();
    }
  }, [showWebPage]);

  useEffect(() => {
    if (sessionEnded) {
      showEndedPanel();
    }
  }, [sessionEnded]);

  // The SDK shows a new single-use QR every ~20 s (onQrUpdate) and reports the
  // session's status from the WebSocket (onStatus). Success and Failure aren't final: each
  // successful check extends the session (a later expiryTime); only Expired ends it. These
  // callbacks only drive the UI: a real integration authorizes with VerifySession from its backend.
  //
  // Secure session ownership: the demo backend creates the session with CreateSession, the app's
  // backend credential and the signed-in ACT.SSO user's account; the SDK passes it only the browser
  // key's hash and fetches every QR code from ScanToken itself (the browser never calls
  // GetSession). Here selectedAPIKey is the demo backend's app id, not an API key.
  const initialiseSDKSession = () =>
    ActGeo.StartWebSession({
      createSession: (browserKeyHash) => createDemoSession(selectedAPIKey, accountId.trim(), browserKeyHash),
      baseUrl: GEO_BASE_URL,
      enableLogging: SDK_LOGGING,
      onQrUpdate: ({ qrCode, url, state }) => {
        // Keep showing the last QR while a new one is fetched; the panels change on status
        if (state !== "active") return;
        setQrCode(qrCode);
        setDeeplink(url);
        setSessionData((previous) => ({ ...previous, qrCode, url }));
      },
      onStatus: ({ status, expiryTime, endReason }) => {
        if (status === null || status === undefined) return; // one-off warnings
        if (expiryTime) setSessionData((previous) => ({ ...previous, expiryTime }));
        const response = { status, expiryTime, endReason };
        switch (status) {
          case 2: // Success (again on every extension, with the later expiryTime)
            setValidationResult(response);
            handleQrCodeScanned();
            showVerifedPanel();
            setValidationStatus(response);
            break;
          case 3: // Failure (a later successful check can still turn it into Success)
            setValidationResult(response);
            handleQrCodeScanned();
            showFailedPanel();
            break;
          case 4: // Expired: the session is over (endReason when the phone ended it early, SID-2471)
            setValidationResult(response);
            handleQrCodeScanned();
            showEndedPanel();
            break;
          default: // 1 Pending: waiting for the phone
            break;
        }
      },
      onError: ({ code }) => {
        // The session ended meanwhile (the page is going to the portal), which resets the page
        if (getSsoState().status !== "signedIn") return;
        setValidationResult({ error: code });
        handleQrCodeScanned();
        showFailedPanel();
      },
    }).then((session) => {
      if (session.sessionId) setSessionData((previous) => ({ ...previous, sessionId: session.sessionId }));
    });

  // Picks up the user's ACT Auth Portal session, if they have one
  useEffect(() => {
    startSso();
  }, []);

  // Signing in, switching user, signing out or the SSO session expiring: the previous user's
  // session (and its QR), jurisdiction and list go first, so nothing of theirs can show or be used.
  // Then the new user's jurisdictions are loaded, only now that they're signed in.
  useEffect(() => {
    const request = ++appsRequest.current;
    ActGeo.StopWebSession();
    handleQrCodeScanned();
    setQrCode(null);
    setDeeplink(null);
    setSessionData(null);
    setValidationResult(null);
    setSelectedAPIKey(null);
    setJurisdictions([]);
    setJurisdictionsError(null);
    setAccountId("");
    showHomePanel();
    if (!sso.user) {
      setLoadingJurisdictions(false);
      return;
    }
    setLoadingJurisdictions(true);
    listDemoApps()
      .then((apps) => {
        if (request !== appsRequest.current) return; // another user since
        setJurisdictions(
          apps.map((app) => ({ ApiKey: app.id, AppName: app.available ? app.name : `${app.name} (no credential)` }))
        );
      })
      .catch((err) => {
        if (request !== appsRequest.current || err.stale) return;
        console.error("[App] Failed to load the jurisdictions:", err.message);
        setJurisdictionsError(err.message);
      })
      .finally(() => {
        if (request === appsRequest.current) setLoadingJurisdictions(false);
      });
  }, [sso.user]);

  const handleQrCodeScanned = () => {
    if (timerRef.current) {
      timerRef.current.stopTimer();
      setQrCode(null);
      setExpiryTime(null);
    }
  };
  const handleTimerExpire = () => {
    setQrCode(null);
    setRefreshQrCode(true);
  };

  const onRefreshQrCode = () => {
    setRefreshQrCode(false);
    setExpiryTime(null);
    setValidationResult(null);
    setSessionData(null);
    initialiseSDKSession(); // the demo backend takes the account from the user's SSO session
  };

  const handleChange = (event) => {
    setRefreshQrCode(false);
    setExpiryTime(null);
    setValidationResult(null);
  };

  const onAppSelect = (value) => {
    // console.log(value);
  };

  const onGenerateQRCode = () => {
    onRefreshQrCode();
  };

  const onGenerateNewQRCode = () => {
    onRefreshQrCode();
    setIsHomePanel(true);
    setIsVerifiedPanel(false);
    setIsFailedPanel(false);
    setIsEndedPanel(false);
  };

  return (
    <Box
      sx={{
        background: "transparent",
      }}
      className="App"
    >
      <Header
        goToHomePanel={goToHomePanel}
        goToHome={goToHome}
        appSelectHandler={onAppSelect}
      />

      {!visible ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            alignItems: "center",
            width: "100%",
            height: isMobile ? "100vh" : "95vh",
            background: "#000000",
            position: "relative",
          }}
        >
          <Box
            onClick={() => toggleVisibilityRef.toggle()}
            sx={{
              backgroundColor: "#ffffff",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "1.5rem",
              width: "1.5rem",
              color: "#000000",
              position: "absolute",
              left: "1rem",
              top: "1rem",
              borderRadius: "50%",
              cursor: "pointer",
              "&:hover": {
                opacity: "0.6",
              },
            }}
          >
            <ChevronLeftIcon />
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              alignItems: "center",
              width: "100%",
              maxWidth: isMobile ? "100%" : "800px",
              margin: isMobile ? "1rem auto" : "1.5rem auto",
            }}
          >
            <Typography
              sx={{
                fontSize: "1.4rem",
                fontWeight: "900",
                margin: "0.5rem auto 0.5rem auto",
                lineHeight: "1.2",
                color: "#d05fb3",
                padding: isMobile ? "2rem 1rem 0 1rem" : "0 1rem",
              }}
            >
              EPL: Pick 4 Teams To Score
            </Typography>
            <Typography
              sx={{
                fontSize: "0.8rem",
                fontWeight: "600",
                margin: isMobile ? "0 auto 0.5rem auto" : "0 auto 1rem auto",
                padding: "0 0.5rem",
                color: "#ffffff",
              }}
            >
              Fri 21st 20:00 - Sat 22nd 20:00
            </Typography>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: "0.5rem",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-start",
                  alignItems: "center",
                }}
              >
                <img src={FootballGraphic} height={"60px"} />
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-start",
                    alignItems: "center",
                    height: "0.15rem",
                    backgroundColor: "#d05fb3",
                    width: "2.5rem",
                    margin: "-0.1rem 0 0.4rem 0",
                  }}
                ></Box>
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    fontWeight: "600",
                    margin: "0 auto 0 auto",
                    color: "#ffffff",
                  }}
                >
                  Pick 1
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-start",
                  alignItems: "center",
                }}
              >
                <img src={FootballGraphic} height={"60px"} />
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-start",
                    alignItems: "center",
                    height: "0.15rem",
                    backgroundColor: "#ffffff",
                    width: "2.5rem",
                    margin: "-0.1rem 0 0.4rem 0",
                  }}
                ></Box>
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    fontWeight: "600",
                    margin: "0 auto 0 auto",
                    color: "#ffffff",
                  }}
                >
                  Pick 2
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-start",
                  alignItems: "center",
                }}
              >
                <img src={FootballGraphic} height={"60px"} />
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-start",
                    alignItems: "center",
                    height: "0.15rem",
                    backgroundColor: "#ffffff",
                    width: "2.5rem",
                    margin: "-0.1rem 0 0.4rem 0",
                  }}
                ></Box>
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    fontWeight: "600",
                    margin: "0 auto 0 auto",
                    color: "#ffffff",
                  }}
                >
                  Pick 3
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-start",
                  alignItems: "center",
                }}
              >
                <img src={FootballGraphic} height={"60px"} />
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-start",
                    alignItems: "center",
                    height: "0.15rem",
                    backgroundColor: "#ffffff",
                    width: "2.5rem",
                    margin: "-0.1rem 0 0.4rem 0",
                  }}
                ></Box>
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    fontWeight: "600",
                    margin: "0 auto 0 auto",
                    color: "#ffffff",
                  }}
                >
                  Pick 4
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                width: isMobile ? "90%" : "50%",
                display: "flex",
                flexDirection: "column",
                gap: isMobile ? "0.5rem" : "1rem",
                margin: "1.75rem auto 0rem auto",
              }}
            >
              <ACTButton
                content="Brentford"
                bgColor="#ffffff"
                textColor="#000000"
                hoverColor="#3e3e3e"
                hoverTextColor="#ffffff"
                border="0.1rem solid #ffffff"
                hoverBorder="0.1rem solid #3e3e3e"
              />
              <ACTButton
                content="Chelsea"
                bgColor="#ffffff"
                textColor="#000000"
                hoverColor="#3e3e3e"
                hoverTextColor="#ffffff"
                border="0.1rem solid #ffffff"
                hoverBorder="0.1rem solid #3e3e3e"
              />
              <ACTButton
                content="Leeds"
                bgColor="#ffffff"
                textColor="#000000"
                hoverColor="#3e3e3e"
                hoverTextColor="#ffffff"
                border="0.1rem solid #ffffff"
                hoverBorder="0.1rem solid #3e3e3e"
              />
              <ACTButton
                content="Brighton & Hove Albion"
                bgColor="#ffffff"
                textColor="#000000"
                hoverColor="#3e3e3e"
                hoverTextColor="#ffffff"
                border="0.1rem solid #ffffff"
                hoverBorder="0.1rem solid #3e3e3e"
              />
              <ACTButton
                content="Liverpool"
                bgColor="#ffffff"
                textColor="#000000"
                hoverColor="#3e3e3e"
                hoverTextColor="#ffffff"
                border="0.1rem solid #ffffff"
                hoverBorder="0.1rem solid #3e3e3e"
              />
            </Box>
            <Box
              sx={{
                width: isMobile ? "60%" : "30%",
                display: "flex",
                flexDirection: "column",
                gap: "1rem",
                margin: "2rem auto 1.5rem auto",
              }}
            >
              <ACTButton
                content="PLAY"
                bgColor="#d05fb3"
                textColor="#ffffff"
                hoverColor="#a3008a"
                hoverTextColor="#ffffff"
                border="0.1rem solid #d05fb3"
                hoverBorder="0.1rem solid #a3008a"
              />
            </Box>
          </Box>
        </Box>
      ) : (
        <>
          {showAlert && (
            <ACTAlertWarning
              message="Please make sure your devices are on the same Wi-Fi network."
              severity="error"
              onHide={handleHideAlert}
            />
          )}
          <Box
            style={backgroundStyle}
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              alignItems: "center",
              width: "100%",
              height: isMobile ? "100%" : "100%",
              minHeight: "95vh",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#ffffff",
                width: "95vw",
                maxWidth: isMobile ? "auto" : "28rem",
                margin: isMobile ? "0.75rem auto" : "1.25rem auto 0 auto",
                padding: isMobile ? "1rem 0 1.25rem 0" : "1.5rem",
                borderRadius: "0.5rem",
                boxShadow: "0 0.25rem 0.625rem rgba(0, 0, 0, 0.3)",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  backgroundColor: "#ffffff",
                  width: isMobile ? "90vw" : "28rem",
                  height: isMobile ? "26rem" : "23.5rem",
                  borderRadius: "0.5rem",
                  position: "relative",
                }}
              >
                {isHomePanel && (
                  <HomePanel
                    sessionData={sessionData}
                    onGenerateQR={onGenerateNewQRCode}
                    validationResult={validationResult}
                    refreshQrCode={refreshQrCode}
                    deeplink={deeplink}
                    defaultAPIKey={selectedAPIKey}
                    sso={sso}
                    jurisdictions={jurisdictions}
                    loadingJurisdictions={loadingJurisdictions}
                    jurisdictionsError={jurisdictionsError}
                  />
                )}
                {isVerifiedPanel && (
                  <VerifiedPanel
                    validationResult={validationResult}
                    goToEndPanel={showEndedPanel}
                  />
                )}
                {isFailedPanel && (
                  <FailedPanel
                    goHomePanel={goToHomePanel}
                    onGenerateQR={onGenerateNewQRCode}
                    refreshQrCode={refreshQrCode}
                  />
                )}
                {isEndedPanel && (
                  <SessionEndedPanel
                    endReason={validationResult?.endReason}
                    goHomePanel={goToHomePanel}
                    onGenerateQR={onGenerateNewQRCode}
                    refreshQrCode={refreshQrCode}
                  />
                )}
              </Box>
              {isHomePanel && (
                <Box sx={{ width: "100%", padding: "0.75rem 1rem 0 1rem" }}>
                  <ACTButton
                    content="Create QR Code"
                    onClick={canCreateQR ? onGenerateNewQRCode : undefined}
                    disabled={!canCreateQR}
                    bgColor="#000000"
                    textColor="#ffffff"
                    hoverColor="#E54F9C"
                    hoverTextColor="#ffffff"
                    border="0.1rem solid #000000"
                    hoverBorder="0.1rem solid #E54F9C"
                  />
                </Box>
              )}
              {/* npm start only: the regulated action, authorized only by the backend */}
              {TEST_BET_ENABLED && sessionData?.sessionId && (
                <TestBetPanel key={sessionData.sessionId} sessionId={sessionData.sessionId} />
              )}
              <AppStoreLinksPanel />
            </Box>
          </Box>
        </>
      )}

      <Footer />
    </Box>
  );
}

export default App;
